import React, { useState, useCallback } from 'react';
import { GoogleMap, useJsApiLoader, MarkerF, MarkerClustererF, InfoWindowF } from '@react-google-maps/api';

const containerStyle = {
	width: '100%',
	height: '600px'
};

const defaultCenter = {
	lat: 19.4326,
	lng: -99.1332
};

const clusterOptions = {
	imagePath: 'https://developers.google.com/maps/documentation/javascript/examples/markerclusterer/m',
	gridSize: 60,
	maxZoom: 15
};

const TrackingOverviewMap = ({ locations }) => {
	const { isLoaded, loadError } = useJsApiLoader({
		id: 'google-map-script',
		googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY
	});

	const [map, setMap] = useState(null);
	const [selectedLocation, setSelectedLocation] = useState(null);

	const onLoad = useCallback((map) => {
		setMap(map);

		// Fit bounds to show all markers with country-level view
		if (locations && locations.length > 0) {
			const bounds = new window.google.maps.LatLngBounds();
			locations.forEach(loc => {
				bounds.extend({ lat: loc.lat, lng: loc.lon });
			});

			// Fit bounds with padding
			map.fitBounds(bounds, { top: 50, right: 50, bottom: 50, left: 50 });

			// After fitBounds, adjust zoom to country level (between 5 and 8)
			window.google.maps.event.addListenerOnce(map, 'idle', () => {
				const currentZoom = map.getZoom();
				// If zoom is too far (seeing world), zoom in to country level
				if (currentZoom < 5) {
					map.setZoom(5);
				}
				// If zoom is too close, zoom out a bit
				else if (currentZoom > 8) {
					map.setZoom(8);
				}
			});
		}
	}, [locations]);

	const onUnmount = useCallback(() => {
		setMap(null);
	}, []);

	const getCenter = () => {
		if (locations && locations.length > 0) {
			return {
				lat: locations[0].lat,
				lng: locations[0].lon
			};
		}
		return defaultCenter;
	};

	const handleMarkerClick = (location) => {
		setSelectedLocation(location);
	};

	const formatDate = (timestamp) => {
		if (!timestamp) return 'N/A';
		const ms = timestamp > 1000000000000 ? timestamp : timestamp * 1000;
		const date = new Date(ms);
		return date.toLocaleString('es-MX', {
			day: '2-digit',
			month: '2-digit',
			year: 'numeric',
			hour: '2-digit',
			minute: '2-digit',
			timeZone: 'America/Mexico_City'
		});
	};

	if (loadError) {
		return (
			<div className="alert alert-danger text-center mt-4">
				Error al cargar Google Maps: {loadError.message}
			</div>
		);
	}

	if (!isLoaded) {
		return (
			<div className="d-flex justify-content-center align-items-center" style={{ height: '600px' }}>
				<div className="spinner-border text-primary" role="status">
					<span className="visually-hidden">Loading map...</span>
				</div>
			</div>
		);
	}

	return (
		<GoogleMap
			mapContainerStyle={containerStyle}
			center={!map ? getCenter() : undefined}
			zoom={10}
			onLoad={onLoad}
			onUnmount={onUnmount}
			options={{ gestureHandling: 'greedy', streetViewControl: false, mapTypeControl: false, fullscreenControl: false, zoomControl: false }}
		>
			{locations && locations.length > 0 && (
				<MarkerClustererF options={clusterOptions}>
					{(clusterer) =>
						locations.map((location, index) => (
							<MarkerF
								key={index}
								position={{ lat: location.lat, lng: location.lon }}
								clusterer={clusterer}
								title={location.route?.code || `Punto ${index + 1}`}
								onClick={() => handleMarkerClick(location)}
							/>
						))
					}
				</MarkerClustererF>
			)}

			{selectedLocation && (
				<InfoWindowF
					position={{ lat: selectedLocation.lat, lng: selectedLocation.lon }}
					onCloseClick={() => setSelectedLocation(null)}
					options={{ disableAutoPan: true }}
				>
					<div style={{ padding: '5px', minWidth: '180px', color: '#000' }}>
						<p className="mb-1"><strong>Ruta:</strong> {selectedLocation.route?.code || 'N/A'}</p>
						<p className="mb-1"><strong>Coords:</strong> {selectedLocation.lat.toFixed(6)}, {selectedLocation.lon.toFixed(6)}</p>
						<p className="mb-1"><strong>Fecha:</strong> {formatDate(selectedLocation.date?.$date || selectedLocation.updated || selectedLocation.created)}</p>
					</div>
				</InfoWindowF>
			)}
		</GoogleMap>
	);
};

export default TrackingOverviewMap;
